/**
 * Module 132 (WP-132.6) — which milestone the in-game awareness surfaces name.
 * Pure: the win popup line (D1) and the home "Next goal" peek (F4) read these,
 * so a test can pin every choice without a HUD.
 *
 * Every number comes from `meta/milestones.ts`, the same functions the
 * Milestones popup reads, so the line, the peek and the popup cannot disagree.
 */
import { MILESTONES } from '../constants/economy';
import { isLevelClaimed, levelCoins, levelKey, levelTarget, levelsForValue, } from './milestones';
import { readyRows } from './milestone-view';
/**
 * The line for one win: `before` is the counters when the previous win line was
 * built (or the save loaded), `state` the counters now.
 *
 * 1. A rung reached since then wins: the highest one (a ★ step outranks a tier),
 *    ties to registry order. That is the medal line.
 * 2. Otherwise the milestone that moved most toward its next rung, measured as a
 *    share of that rung's span (so blocks cleared, which moves by tens, does not
 *    always beat wins, which moves by one).
 * 3. Nothing moved: the one closest to its next rung.
 *
 * One-off badges (How to Play) are left out: no win ever moves them. Null when
 * nothing is left to show.
 */
export function pickWinLine(before, state, legacy) {
    let earned = null;
    let moved = null;
    let closest = null;
    for (const def of MILESTONES) {
        if (def.every <= 0)
            continue;
        const after = state.counters[def.id] ?? 0;
        const was = Math.min(before[def.id] ?? 0, after);
        const reachedBefore = levelsForValue(def, was);
        const reachedNow = levelsForValue(def, after);
        if (reachedNow > reachedBefore) {
            const index = reachedNow - 1;
            if (!earned || index > earned.index) {
                const key = levelKey(def, index);
                const base = index === 0 ? 0 : levelTarget(def, index - 1);
                earned = {
                    id: def.id,
                    kind: 'earned',
                    index,
                    coins: levelCoins(def, index),
                    claimable: levelCoins(def, index) > 0 && !isLevelClaimed(state, key, legacy),
                    from: Math.max(base, was),
                    to: levelTarget(def, index),
                    base,
                    target: levelTarget(def, index),
                };
            }
            continue;
        }
        const index = reachedNow;
        const target = levelTarget(def, index);
        const base = index === 0 ? 0 : levelTarget(def, index - 1);
        const span = Math.max(1, target - base);
        const line = {
            id: def.id,
            kind: 'progress',
            index,
            coins: levelCoins(def, index),
            claimable: false,
            from: Math.max(base, was),
            to: after,
            base,
            target,
        };
        if (after > was) {
            const share = (after - was) / span;
            if (!moved || share > moved.share)
                moved = { line, share };
        }
        const share = (after - base) / span;
        if (!closest || share > closest.share)
            closest = { line, share };
    }
    return earned ?? moved?.line ?? closest?.line ?? null;
}
/**
 * The newest ready level, the first in the Milestones popup's own order (WP-132.10:
 * so the peek names the newest badge, and its Claim pays the popup's
 * top Claim); `more` is how many others wait. Otherwise the open ladder closest to
 * its next rung, ties to registry order. One-offs and finished rows are never a
 * goal. Null when there is nothing to show.
 */
export function pickHomePeek(view) {
    const waiting = readyRows(view.rows);
    if (waiting[0])
        return { kind: 'ready', row: waiting[0], more: view.ready.length - 1 };
    let goal = null;
    for (const row of view.rows) {
        if (row.oneOff || row.done || row.next === null)
            continue;
        if (!goal || row.pct > goal.pct)
            goal = row;
    }
    return goal ? { kind: 'goal', row: goal } : null;
}
